<script setup>
import Spinner from "@/components/Spinner.vue"
import { storeToRefs } from "pinia";
import { useUserStore } from "@/stores/user";
import { ref } from "vue";

defineProps({
  siteAnswer: {type: Object},
  llmAnswer: {type: Object},
})
const userStore = useUserStore();
const {user} = storeToRefs(userStore);

const showFullMatchesReason = ref(false);
const showFullAnswerResponse = ref(false);
const showFullAnswerReason = ref(false);

function shouldTruncate(text, limit = 150) {
  return text && text.length > limit;
}
</script>

<template>
  <div v-if="llmAnswer.source === 'gemini' && llmAnswer.status === 'skipped'"
       class="h-100 answer-card border rounded-3 bg-white shadow-sm overflow-hidden d-flex flex-column border-light-subtle">
    <div class="p-2 border-bottom bg-light-subtle">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-2">
          <div
              class="provider-icon-wrapper rounded-circle border p-1 bg-white d-flex align-items-center justify-content-center shadow-sm"
              style="width: 28px; height: 28px;">
            <img :src="llmAnswer.logo" width="18" height="18" :alt="llmAnswer.model">
          </div>
          <div>
            <div class="fw-bold text-dark" style="font-size: 0.75rem; line-height: 1.2;">
              {{ llmAnswer.model }}
            </div>
            <div class="text-muted" style="font-size: 0.6rem;">
               {{ llmAnswer.subtitle }}
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center gap-1">

        </div>
      </div>
    </div>
    <div class="p-3 flex-grow-1 d-flex flex-column">
          <div v-if="!user.plan.trackGemini" class="notification-block-small mb-0" style="font-size: 0.7rem; padding: 5px;">Upgrade to Pro
            to track Gemini.
          </div>
          <div v-else class="notification-block-small mb-0" style="font-size: 0.7rem; padding: 5px;">
            Run another check to track Gemini.
          </div>
      </div>
  </div>

  <div v-else-if="llmAnswer"
       class="answer-card border rounded-3 bg-white shadow-sm overflow-hidden d-flex flex-column h-100"
       :class="{'border-success': llmAnswer.matchesCanonical && llmAnswer.answered,
                              'border-warning': !llmAnswer.matchesCanonical && llmAnswer.answered,
                              'border-danger': llmAnswer.status === 'completed' && !llmAnswer.answered}">
    <div class="p-2 border-bottom bg-light-subtle">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-2">
          <div
              class="provider-icon-wrapper rounded-circle border p-1 bg-white d-flex align-items-center justify-content-center shadow-sm"
              style="width: 28px; height: 28px;">
            <img :src="llmAnswer.logo" width="18" height="18" :alt="llmAnswer.model">
          </div>
          <div>
            <div class="fw-bold text-dark" style="font-size: 0.75rem; line-height: 1.2;">
              {{ llmAnswer.model }}
            </div>
            <div class="text-muted" style="font-size: 0.6rem;">
              {{ llmAnswer.subtitle }}
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center gap-1">
          <template v-if="['pending', 'in_progress'].includes(llmAnswer.status)">
            <spinner></spinner>
          </template>
          <template
              v-else-if="llmAnswer.status === 'completed' && !llmAnswer.answered">
                          <span
                              class="badge bg-soft-warning text-warning border border-warning border-opacity-25 rounded-pill px-2"
                              style="font-size: 0.6rem;"
                          >
                            <i class="fa-solid fa-circle-question"></i> Not Answered
                          </span>
          </template>
          <div style="font-size: 10px"
               v-else-if="llmAnswer.status === 'completed' && !llmAnswer.canonicalChecked">
            <spinner></spinner>
            <span class="text-muted">Validating..</span>
          </div>
          <span v-else-if="llmAnswer.matchesCanonical"
                class="badge bg-soft-success text-success border border-success border-opacity-25 py-1 px-2 rounded-pill"
                style="font-size: 0.6rem;">
                          <i class="fa-solid fa-check me-1"></i> Match
                        </span>
          <span v-else
                class="badge bg-soft-warning text-warning border border-warning border-opacity-25 rounded-pill px-2"
                style="font-size: 0.6rem;">
                          <i class="fa-solid fa-triangle-exclamation"></i> Answer Mismatch
                        </span>
        </div>
      </div>
    </div>

    <div class="p-3 flex-grow-1 d-flex flex-column">
      <div class="answer-text mb-3"
           style="font-size: 0.75rem; line-height: 1.5;">
        <template v-if="['pending', 'in_progress'].includes(siteAnswer.status)">
          <div class="d-flex align-items-center gap-2 text-muted italic">
            <spinner></spinner>
            <span>Waiting for Page..</span>
          </div>
        </template>
        <template v-else-if="['pending', 'in_progress'].includes(llmAnswer.status)">
          <div class="d-flex align-items-center gap-2 text-muted italic">
            <spinner></spinner>
            <span>Asking {{ llmAnswer.model }}..</span>
          </div>
        </template>
        <template v-else>
          <template v-if="!llmAnswer.answered">
            <div class="text-warning-emphasis fw-medium d-flex align-items-start gap-2 small italic">
              <i class="fa-solid fa-circle-info mt-1"></i>
              <span v-if="llmAnswer.source === 'site'">   Answer not found on this page. </span>
              <span v-else>   {{ llmAnswer.model }} could not answer this question. </span>
            </div>
            <div v-if="llmAnswer.answerReason"
                 :class="{'border-success': llmAnswer.foundOnPage, 'border-warning': !llmAnswer.foundOnPage}"
                 class="mt-2 p-2 rounded bg-light border-start border-3"
                 style="font-size: 0.7rem;">
              <span class="text-muted italic">
                <template v-if="showFullAnswerReason || !shouldTruncate(llmAnswer.answerReason, 100)">
                  {{ llmAnswer.answerReason }}
                </template>
                <template v-else>
                  {{ llmAnswer.answerReason.substring(0, 100) }}...
                </template>
                <button
                    v-if="shouldTruncate(llmAnswer.answerReason, 100)"
                    class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                    style="font-size: 0.7rem; text-decoration: none;"
                    @click.stop="showFullAnswerReason = !showFullAnswerReason"
                >
                  {{ showFullAnswerReason ? 'Show less' : 'Read more' }}
                </button>
              </span>
            </div>
          </template>
          <div v-else>
            <div class="">
              <template
                  v-if="showFullAnswerResponse || !shouldTruncate(llmAnswer.response)">
               {{ llmAnswer.response }}
              </template>
              <template v-else>
                {{ llmAnswer.response.substring(0, 150) }}...
              </template>
              <button
                  v-if="shouldTruncate(llmAnswer.response)"
                  class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                  style="font-size: 0.7rem; text-decoration: none;"
                  @click.stop="showFullAnswerResponse = !showFullAnswerResponse"
              >
                {{ showFullAnswerResponse ? 'Show less' : 'Read more' }}
              </button>
            </div>
            <div v-if="llmAnswer.answerReason && llmAnswer.matchesCanonical"
                 :class="{'border-success': llmAnswer.answered, 'border-warning': !llmAnswer.answered}"
                 class="mt-2 p-2 rounded bg-light border-start border-3"
                 style="font-size: 0.7rem;">
              <span class="text-muted italic">
                <template v-if="showFullAnswerReason || !shouldTruncate(llmAnswer.answerReason, 100)">
                  {{ llmAnswer.answerReason }}
                </template>
                <template v-else>
                  {{ llmAnswer.answerReason.substring(0, 100) }}...
                </template>
                <button
                    v-if="shouldTruncate(llmAnswer.answerReason, 100)"
                    class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                    style="font-size: 0.7rem; text-decoration: none;"
                    @click.stop="showFullAnswerReason = !showFullAnswerReason"
                >
                  {{ showFullAnswerReason ? 'Show less' : 'Read more' }}
                </button>
              </span>
            </div>
            <div
                v-if="!llmAnswer.matchesCanonical && llmAnswer.matchesCanonicalReason"
                class="mt-2 p-2 rounded bg-warning-subtle border-start border-warning border-3"
                style="font-size: 0.7rem;">
              <span class="text-danger opacity-75 italic">
                <template
                    v-if="showFullMatchesReason || !shouldTruncate(llmAnswer.matchesCanonicalReason, 100)">
                  {{ llmAnswer.matchesCanonicalReason }}
                </template>
                <template v-else>
                  {{ llmAnswer.matchesCanonicalReason.substring(0, 100) }}...
                </template>
                <button
                    v-if="shouldTruncate(llmAnswer.matchesCanonicalReason, 100)"
                    class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                    style="font-size: 0.7rem; text-decoration: none;"
                    @click.stop="showFullMatchesReason = !showFullMatchesReason"
                >
                  {{ showFullMatchesReason ? 'Show less' : 'Read more' }}
                </button>
              </span>
            </div>
          </div>
        </template>
      </div>

      <div v-if="llmAnswer.history" class="pt-2 border-top mt-auto">
        <div class="d-flex align-items-center justify-content-between">
                        <span style="font-size:10px;"
                              class="text-muted fw-semibold uppercase tracking-wider">History</span>
          <div class="d-flex gap-1 align-items-center">
                          <span
                              v-for="historyItem in llmAnswer.history"
                              class="history-dot-sm"
                              :style="{'background-color': historyItem.color}"
                              v-tooltip="historyItem.status"
                          ></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.tracking-wider {
  letter-spacing: 0.05em;
}

.uppercase {
  text-transform: uppercase;
}

.btn-xs {
  padding: 0.1rem 0.4rem;
  font-size: 0.75rem;
}




.answer-card {
  transition: all 0.2s ease-in-out;
}

.answer-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-1px);
}

.history-dot-sm {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.history-dot-sm:hover {
  transform: scale(1.3);
}


.bg-soft-success {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.text-warning-emphasis {
  color: #664d03 !important;
}

.answer-text {
  color: #495057;
}


</style>